import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  Collapse,
  Snackbar,
  Alert,
} from "@mui/material";
import BioSpecimenWizard from "./BioSpecimenWizard";
import BackButton from "./BackButton";
import ForwardButton from "./ForwardButton";
import { useWizardContext } from "./WizardContext";
import { collectSamples } from "utils/apiBiospecimen";
import { fetchAdminSampleTypeByLetter } from "utils/api";
import { BIOBANK_SUB_ROUTES } from "constants/routes";

const QUALITY_OPTIONS = ["Excellent", "Good", "Fair"];

const CommentsAndSubmit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { encounterId, batchId } = useParams();
  const { formData, updateFormData } = useWizardContext();

  const encounterData = location.state?.encounterData || {};
  const batch = encounterData?.batch || {};

  // Use remainingSampleTypes for the samples that need to be collected
  const remainingSampleTypes = useMemo(() => batch.remainingSampleTypes || [], [batch.remainingSampleTypes]);

  // Fetch sample type details for display names
  const [sampleTypeDetails, setSampleTypeDetails] = useState({});
  const [loadingSampleTypes, setLoadingSampleTypes] = useState(true);

  // Initialize form state - always start fresh, don't use cached formData
  const [samples, setSamples] = useState(() => {
    const initial = {};
    remainingSampleTypes.forEach(letter => {
      initial[letter] = {
        selected: false,
        complete: "Yes",
        quality: "Excellent",
        comments: ""
      };
    });
    return initial;
  });

  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Fetch sample type details
  useEffect(() => {
    const fetchSampleTypeDetails = async () => {
      try {
        setLoadingSampleTypes(true);
        const details = {};
        await Promise.all(
          remainingSampleTypes.map(async (letter) => {
            try {
              const sampleType = await fetchAdminSampleTypeByLetter(letter);
              details[letter] =
                sampleType || {
                  sampleLetter: letter,
                  longName: `Sample ${letter}`,
                };
            } catch (err) {
              console.error(`Error fetching sample type ${letter}:`, err);
              details[letter] = { sampleLetter: letter, longName: `Sample ${letter}` };
            }
          })
        );
        setSampleTypeDetails(details);
      } catch (err) {
        console.error("Error fetching sample types:", err);
      } finally {
        setLoadingSampleTypes(false);
      }
    };

    if (remainingSampleTypes.length > 0) {
      fetchSampleTypeDetails();
    } else {
      setLoadingSampleTypes(false);
    }
  }, [remainingSampleTypes]);

  // Save to context whenever samples change
  useEffect(() => {
    updateFormData({ samples });
  }, [samples, updateFormData]);

  const handleCheckboxChange = (sampleLetter, checked) => {
    setSamples(prev => ({
      ...prev,
      [sampleLetter]: {
        ...prev[sampleLetter],
        selected: checked
      }
    }));
  };

  const handleFieldChange = (sampleLetter, field, value) => {
    setSamples(prev => ({
      ...prev,
      [sampleLetter]: {
        ...prev[sampleLetter],
        [field]: value
      }
    }));
  };

  // Validation
  const validateForm = () => {
    const selectedSamples = Object.keys(samples).filter(letter => samples[letter].selected);

    if (selectedSamples.length === 0) {
      return { valid: false, message: "Please select at least one sample to collect." };
    }

    for (const letter of selectedSamples) {
      const sample = samples[letter];

      if (!sample.complete) {
        return { valid: false, message: `Please specify if sample ${letter} was collected completely.` };
      }

      if (!sample.quality) {
        return { valid: false, message: `Please select quality for sample ${letter}.` };
      }

      if (sample.complete === "No" && !sample.comments.trim()) {
        return { valid: false, message: `Comments are required for sample ${letter} when collection is incomplete.` };
      }
    }

    return { valid: true };
  };

  const handleBack = () => {
    navigate(`/biobank/sample-collection/encounters/${encounterId}/batches/${batchId}/authorization`, {
      state: location.state
    });
  };

  const handleSubmit = async () => {
    const validation = validateForm();

    if (!validation.valid) {
      setSnackbar({ open: true, message: validation.message, severity: "error" });
      return;
    }

    // Prepare samples payload
    const samplesToCollect = [];
    Object.keys(samples).forEach(letter => {
      if (samples[letter].selected) {
        samplesToCollect.push({
          sampleLetter: letter,
          sampleQuality: samples[letter].quality,
          sampleComments: samples[letter].comments,
          complete: samples[letter].complete
        });
      }
    });

    if (samplesToCollect.length === 0) {
      setSnackbar({ open: true, message: "Please select at least one sample to collect.", severity: "error" });
      return;
    }

    setSubmitting(true);
    try {
      await collectSamples(encounterId, batchId, samplesToCollect);

      // Clear form data from session storage
      sessionStorage.removeItem("bioSpecimenFormData");

      setSnackbar({ open: true, message: "Samples collected successfully!", severity: "success" });

      // Navigate back to encounter plan after a short delay
      setTimeout(() => {
        navigate(BIOBANK_SUB_ROUTES.SAMPLE_COLLECTION_PLAN(encounterId), {
          state: { sampleCollected: true }
        });
      }, 1000);
    } catch (err) {
      console.error("Error collecting samples:", err);
      const errorMessage = err.response?.data?.message
        || err.message
        || "Failed to submit collection. Please try again.";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (remainingSampleTypes.length === 0) {
    return (
      <BioSpecimenWizard>
        <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
          <Alert severity="info">
            All samples for this batch have been collected. No remaining samples to collect.
          </Alert>
        </Box>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <BackButton onClick={handleBack} />
        </Stack>
      </BioSpecimenWizard>
    );
  }

  return (
    <BioSpecimenWizard>
      <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
        <Typography variant="h5" gutterBottom fontWeight={600} sx={{ color: "#500000", mb: 4, textAlign: "center" }}>
          Leave Comments & Submit Samples
        </Typography>

        <Stack spacing={3}>
          {remainingSampleTypes.map((sampleLetter) => {
            const sample = samples[sampleLetter];
            const isSelected = sample?.selected || false;
            const sampleTypeInfo = sampleTypeDetails[sampleLetter];
            const displayName = sampleTypeInfo?.longName || sampleTypeInfo?.shortName || `Sample ${sampleLetter}`;

            return (
              <Card
                key={sampleLetter}
                variant="outlined"
                sx={{
                  border: "1px solid #e0e0e0",
                  boxShadow: "none",
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleCheckboxChange(sampleLetter, e.target.checked)}
                        sx={{
                          color: "#500000",
                          '&.Mui-checked': {
                            color: "#500000",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="h6" fontWeight={600}>
                        {sampleLetter} - {displayName}
                      </Typography>
                    }
                  />

                  <Collapse in={isSelected}>
                    <Box sx={{ mt: 1.5 }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 2, mb: 1.5 }}>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Were you able to collect a complete sample? <span style={{ color: 'red' }}>*</span>
                          </Typography>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={sample?.complete || "Yes"}
                            onChange={(e) => handleFieldChange(sampleLetter, "complete", e.target.value)}
                          >
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </TextField>
                        </Box>

                        <Box>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Select Sample Quality <span style={{ color: 'red' }}>*</span>
                          </Typography>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={sample?.quality || "Excellent"}
                            onChange={(e) => handleFieldChange(sampleLetter, "quality", e.target.value)}
                          >
                            {QUALITY_OPTIONS.map((quality) => (
                              <MenuItem key={quality} value={quality}>
                                {quality}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {sample?.complete === "No"
                            ? (<>If not, please briefly describe what happened. <span style={{ color: 'red' }}>*</span></>)
                            : "Please leave any necessary comments about the sample, if applicable."
                          }
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={1}
                          value={sample?.comments || ""}
                          onChange={(e) => handleFieldChange(sampleLetter, "comments", e.target.value)}
                          required={sample?.complete === "No"}
                          error={sample?.complete === "No" && !sample?.comments?.trim()}
                          helperText={
                            sample?.complete === "No" && !sample?.comments?.trim()
                              ? "This field is required when Complete is 'No'."
                              : ""
                          }
                        />
                      </Box>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Box>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <BackButton onClick={handleBack} disabled={submitting} />
        <ForwardButton
          label="SUBMIT"
          onClick={handleSubmit}
          disabled={submitting}
        />
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </BioSpecimenWizard>
  );
};

export default CommentsAndSubmit;
